import { SubsidiariesTotalsDto } from 'api/actor';
import { NsKey } from 'enums/NsKey';
import { LegalEntityFormFieldNames } from 'features/new-actor/enums/legalEntityFormFieldNames';
import { financialsBlockSchemaFields } from 'features/new-actor/form/validation-rules/optim/legalEntityValidationRules';
import {
  mapFinancialsToCommand,
  mapSubsidiariesResponse,
  SubsidiariesFormData
} from 'features/new-actor/mappers/optim/mapSubsidiaries';
import { LegalEntityFormValues } from 'features/new-actor/types/optim/legalEntityFormValues';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { actorsAPI } from 'services/swagger-api';
import { risksAPI } from 'services/swagger-api/riskApi';
import { runOperation } from 'shared/utils/runOperation';
import * as Yup from 'yup';

export interface LoadedSubsidiaries extends SubsidiariesFormData {
  /** Cumulated figures computed by the back, never recomputed here. */
  totals: SubsidiariesTotalsDto;
}

export type FinancialsSyncValues = Pick<LegalEntityFormValues, LegalEntityFormFieldNames.Financials>;

export interface SubsidiariesSyncMessages {
  /** Shown when the API fails without a message of its own. */
  saveError: string;
  /** Shown when the form values are incomplete: nothing is sent. */
  invalidValues: string;
}

const financialsSyncSchema = Yup.object(financialsBlockSchemaFields);

/** Accounting data and subsidiaries of a legal entity, as saved. */
export async function loadSubsidiaries(legalEntityId: string): Promise<LoadedSubsidiaries> {
  const response = await actorsAPI.getSubsidiaries({ personneMoraleId: legalEntityId });
  return { ...mapSubsidiariesResponse(response), totals: response.totals };
}

/** Legal entity registered on the risk (subscription process), read back after its registration. */
export async function getRiskPersonId(riskId: string): Promise<string | undefined> {
  const response = await risksAPI.getSubscriptionProcess({ riskId });
  const riskData = response?.riskData;
  return riskData && 'person' in riskData ? riskData.person?.id ?? undefined : undefined;
}

/**
 * Saves the accounting data of the legal entity form: one call, the financials upsert. The
 * subsidiaries are never touched here, they are saved row by row by their own routes.
 * Shows the failure and returns false.
 */
export async function syncFinancials(
  legalEntityId: string,
  values: FinancialsSyncValues,
  messages: SubsidiariesSyncMessages
): Promise<boolean> {
  // Mappers turn a missing number into 0: never send incomplete accounting data.
  if (!values.financials || !(await financialsSyncSchema.isValid(values))) {
    toast.error(messages.invalidValues);
    return false;
  }

  const financials = values.financials;
  return runOperation(
    () => actorsAPI.updateLegalEntityFinancials({
      personneMoraleId: legalEntityId,
      updateLegalEntityFinancialsCommandDto: mapFinancialsToCommand(financials)
    }),
    messages.saveError
  );
}

export const useSubsidiariesSync = () => {
  const { t } = useDynamicTranslation([NsKey.ActorForm]);

  const messages = useMemo<SubsidiariesSyncMessages>(() => ({
    saveError: t('ACTOR_FORM.LEGAL_ENTITY.SUBSIDIARIES.SYNC.SAVE_ERROR'),
    invalidValues: t('ACTOR_FORM.LEGAL_ENTITY.SUBSIDIARIES.SYNC.INVALID_VALUES')
  }), [t]);

  const syncFinancialsWithMessages = useCallback(
    (legalEntityId: string, values: FinancialsSyncValues) => syncFinancials(legalEntityId, values, messages),
    [messages]
  );

  return {
    loadSubsidiaries,
    syncFinancials: syncFinancialsWithMessages,
    getRiskPersonId
  };
};
